import { VenueInfoItem } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';

/**
 * Display-only venue-name helpers (tcx-core-platforms-903 D2).
 *
 * A coordinator may set a `canonicalVenueName` override on a venue. It affects DISPLAY only —
 * selection, routing, scoping, and identity always stay on the composite (conference, venue) key.
 * These helpers centralize the two display conventions so the Launches and Groups list pages render
 * venue names identically (and so the formatting is unit-testable without standing up a page).
 */

/**
 * The single D2 fallback rule for one venue: the coordinator's canonical override when set AND
 * non-blank (whitespace-only overrides don't count — they'd render as blank spaces), else the raw
 * venue. Centralized here so every venue-facing surface (table cells, filter dropdowns, the venue
 * build page) applies the same trimmed fallback rather than each inlining `canonicalVenueName || venue`
 * (which mishandles a whitespace-only override). tcx-core-platforms-903 D2 / finding on CR-301380769.
 *
 * @param canonicalVenueName the coordinator's display override (may be undefined/blank)
 * @param venue the raw venue name
 * @returns the trimmed canonical override if non-blank, else the raw venue, else '' when both absent
 */
export function canonicalOrRawVenue(
  canonicalVenueName?: string,
  venue?: string
): string {
  const canonical = canonicalVenueName?.trim();
  return canonical || venue || '';
}

/**
 * Plain display name for a venue: the coordinator's canonical override when set (and non-blank),
 * else the raw venue name. Used for table cells / detail views where only one name is shown.
 *
 * @param venues the venue list fetched from ListVenues
 * @param conference the venue's conference (part of the composite identity key)
 * @param venue the raw venue name
 * @returns the canonical override if present, else the raw venue, else '-' when venue is missing
 */
export function venueDisplayName(
  venues: VenueInfoItem[],
  conference?: string,
  venue?: string
): string {
  if (!venue) return '-';
  const match = venues.find(
    (v) => v.conference === conference && v.venue === venue
  );
  return canonicalOrRawVenue(match?.canonicalVenueName, venue);
}

/**
 * Filter-dropdown label for a venue within a selected conference. When a coordinator canonical
 * override exists (and differs from the raw name), disambiguate as "<canonicalVenueName> (<venue>)" so
 * the filter surfaces BOTH the friendly name and the true venue; otherwise just the raw venue. The
 * dropdown option's VALUE stays the raw venue, so scoping/identity are unchanged — this is label-only.
 *
 * @param venues the venue list fetched from ListVenues
 * @param conference the currently-selected conference (part of the composite identity key)
 * @param venue the raw venue name
 * @returns "<canonical> (<venue>)" when an override differs from the raw name, else the raw venue
 */
export function venueOptionLabel(
  venues: VenueInfoItem[],
  conference: string | undefined,
  venue: string
): string {
  const match = venues.find(
    (v) => v.conference === conference && v.venue === venue
  );
  const canonical = match?.canonicalVenueName?.trim();
  return canonical && canonical !== venue ? `${canonical} (${venue})` : venue;
}

// NUL delimiter for the composite (conference, venue) identity key. A control character that can't
// appear in a real conference/venue name, so the two fields can never be confused at the boundary.
const COMPOSITE_VENUE_KEY_DELIMITER = '\u0000';

/**
 * Build the composite (conference, venue) identity key. Selection, routing, and equality checks key
 * on this — NOT on the display name — so two conferences that share a venue NAME never collide
 * (tcx-core-platforms-903 F1/F2/F4). Centralized here (next to the display helpers) so the delimiter
 * and field order live in one place: an inlined copy that changed either would silently break
 * equality against the others with no compiler error (finding on CR-301380769). Mirrors the backend
 * `PackageOwner.conferenceVenueKey`.
 *
 * @param conference the venue's conference
 * @param venue the raw venue name
 * @returns "<conference>\u0000<venue>"
 */
export function compositeVenueKey(
  conference: string | undefined,
  venue: string | undefined
): string {
  return `${conference ?? ''}${COMPOSITE_VENUE_KEY_DELIMITER}${venue ?? ''}`;
}
